/// <amd-module name="Repos/VersionDropdown/GitRefService" />
import { GitCommitRef, GitQueryCommitsCriteria, GitRef } from "Repos/Common/Contracts/Git";
import { branchNameToRef, isBranch, tagNameToRef } from "Repos/Common/Util/Ref";
import { getGitClient } from "Repos/VersionDropdown/RestClient/Git";
import { IReadyableReadonlyObservableArray, ReadyableObservableArray } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { PagedList } from "VSS/Platform/Contracts/WebApi";

/**
 * Service for working with git refs (branches and tags)
 */
export interface IGitRefService extends IVssService {
    /**
     * Invalidates and refreshes any cached branches
     */
    flushBranchesCache(repositoryId?: string): void;

    /**
     * Attempts to get the specified branch by name
     *
     * @param repositoryId Id of the repository
     * @param projectId Id of the project
     * @param branchName Name of the branch
     */
    getBranchByName(repositoryId: string, projectId: string, branchName: string): Promise<GitRef | undefined>;

    /**
     * Gets all commits that begin with the provided Id.
     */
    getCommitsById(repositoryId: string, projectId: string, id: string): PagedResults<GitCommitRef>;

    /**
     * Get all the branches with another page loaded. It will use cache to return more pages.
     * If cache was cleared since previous call, it will silently start over returning fresh results.
     */
    getMoreBranches(repositoryId: string, projectId: string, continuationToken: string): PagedResults<GitRef>;

    /**
     * Get all the tags with another page loaded. It will use cache to return more pages.
     * If cache was cleared since previous call, it will silently start over returning fresh results.
     */
    getMoreTags(repositoryId: string, projectId: string, continuationToken: string): PagedResults<GitRef>;

    /**
     * Get all the branches of current user, no pagination. It will use cache.
     */
    getMyBranches(repositoryId: string, projectId: string): IReadyableReadonlyObservableArray<GitRef>;

    /**
     * Get the first page of branches. It may use cache to return more pages at once.
     * It won't fetch if there's at least one page fetched.
     */
    getPagedBranches(repositoryId: string, projectId: string): PagedResults<GitRef>;

    /**
     * Get the first page of tags. It may use cache to return more pages at once.
     * It won't fetch if there's at least one page fetched.
     */
    getPagedTags(repositoryId: string, projectId: string): PagedResults<GitRef>;

    /**
     * Attempts to get the specified tag by name
     *
     * @param repositoryId Id of the repository
     * @param projectId Id of the project
     * @param tagName Name of the tag
     */
    getTagByName(repositoryId: string, projectId: string, tagName: string): Promise<GitRef | undefined>;

    /**
     * Registers a callback to notify when branches change.
     */
    registerBranchesCacheListener(handler: () => void): void;

    /**
     * Registers a callback to notify when tags change.
     */
    registerTagsCacheListener(handler: () => void): void;

    /**
     * Gets the branches that match the given search text. It may be a partial result (first page).
     * @param continuationToken If provided, results will start where they were left on a previous call.
     */
    searchBranches(repositoryId: string, projectId: string, searchText: string, continuationToken?: string): PagedResults<GitRef>;

    /**
     * Gets the tags that match the given search text. It may be a partial result (first page).
     * @param continuationToken If provided, results will start where they were left on a previous call.
     */
    searchTags(repositoryId: string, projectId: string, searchText: string, continuationToken?: string): PagedResults<GitRef>;

    /**
     * Unregisters a callback to notify when branches change.
     */
    unregisterBranchesCacheListener(handler: () => void): void;

    /**
     * Unregisters a callback to notify when tags change.
     */
    unregisterTagsCacheListener(handler: () => void): void;
}

type DictionaryOfRefs = { [repositoryId: string]: PagedResults<GitRef> };

const pageSize = 200;

const enum RefArea {
    heads = "heads",
    tags = "tags"
}

export interface PagedResults<T> {
    /**
     * The items returned from the api.
     */
    items: IReadyableReadonlyObservableArray<T>;
    /**
     * A string that can be passed to the same endpoint that returned this PagedResults in order to retrieve the next page of results.
     */
    continuationToken?: string | null;
}

class GitRefService extends VssService implements IGitRefService {
    private myBranchesByRepository: { [repositoryId: string]: IReadyableReadonlyObservableArray<GitRef> } = {};
    private branchesByRepository: DictionaryOfRefs = {};
    private tagsByRepository: DictionaryOfRefs = {};
    private refsByArea = {
        [RefArea.heads]: this.branchesByRepository,
        [RefArea.tags]: this.tagsByRepository
    };

    private readonly branchesCacheListeners: Array<() => void> = [];
    private readonly tagsCacheListeners: Array<() => void> = [];

    public flushBranchesCache(repositoryId?: string) {
        if (repositoryId) {
            delete this.branchesByRepository[repositoryId];
            delete this.myBranchesByRepository[repositoryId];
        } else {
            this.branchesByRepository = {};
            this.refsByArea[RefArea.heads] = this.branchesByRepository;
            this.myBranchesByRepository = {};
        }
    }

    public getBranchByName(repositoryId: string, projectId: string, branchName: string): Promise<GitRef | undefined> {
        const branchRefName = branchNameToRef(branchName);

        // Look in existing my branches first
        let branchRef = this.getMyBranches(repositoryId, projectId).value.filter(ref => equals(ref.name, branchRefName, false))[0];
        if (!branchRef) {
            // Look in the (cached) paged branches
            branchRef = this.getPagedBranches(repositoryId, projectId).items.value.filter(ref => equals(ref.name, branchRefName, false))[0];
            if (!branchRef) {
                // Explicitly look up the specified branch
                return getGitClient(this.pageContext)
                    .getRefs(
                        repositoryId,
                        projectId,
                        RefArea.heads + "/" + branchName,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        pageSize
                    )
                    .then((pagedList: PagedList<GitRef>) => {
                        return pagedList.filter(ref => equals(ref.name, branchRefName, false))[0];
                    });
            }
        }

        return Promise.resolve(branchRef);
    }

    public getMyBranches(repositoryId: string, projectId: string): IReadyableReadonlyObservableArray<GitRef> {
        if (!this.myBranchesByRepository[repositoryId]) {
            const items = new ReadyableObservableArray<GitRef>();
            this.myBranchesByRepository[repositoryId] = items;
            getGitClient(this.pageContext)
                .getRefs(repositoryId, projectId, undefined, false, false, true)
                .then(refs => {
                    items.value = refs.filter(ref => isBranch(ref.name));
                    items.ready.value = true;
                });
        }

        return this.myBranchesByRepository[repositoryId];
    }

    public getPagedBranches(repositoryId: string, projectId: string): PagedResults<GitRef> {
        return this.getPagedRefs(repositoryId, projectId, RefArea.heads);
    }

    public getMoreBranches(repositoryId: string, projectId: string, continuationToken: string): PagedResults<GitRef> {
        return this.getMoreRefs(repositoryId, projectId, continuationToken, RefArea.heads);
    }

    public getTagByName(repositoryId: string, projectId: string, tagName: string): Promise<GitRef | undefined> {
        const tagRefName = tagNameToRef(tagName);

        // Look in existing my branches first
        const tagRef = this.getPagedTags(repositoryId, projectId).items.value.filter(ref => equals(ref.name, tagRefName, true))[0];
        if (tagRef) {
            return Promise.resolve(tagRef);
        }

        return getGitClient(this.pageContext)
            .getRefs(
                repositoryId,
                projectId,
                RefArea.tags + "/" + tagName,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                pageSize
            )
            .then((pagedList: PagedList<GitRef>) => {
                return pagedList.filter(ref => equals(ref.name, tagRefName, true))[0];
            });
    }

    public searchBranches = (repositoryId: string, projectId: string, searchText: string, continuationToken?: string): PagedResults<GitRef> =>
        this.searchPagedRefs(repositoryId, projectId, searchText, RefArea.heads, continuationToken);

    public getPagedTags(repositoryId: string, projectId: string): PagedResults<GitRef> {
        return this.getPagedRefs(repositoryId, projectId, RefArea.tags);
    }

    public getMoreTags(repositoryId: string, projectId: string, continuationToken: string): PagedResults<GitRef> {
        return this.getMoreRefs(repositoryId, projectId, continuationToken, RefArea.tags);
    }

    public registerBranchesCacheListener(handler: () => void): void {
        this.branchesCacheListeners.push(handler);
    }

    public unregisterBranchesCacheListener(handler: () => void): void {
        const index = this.branchesCacheListeners.indexOf(handler);
        if (index >= 0) {
            this.branchesCacheListeners.slice(index, 1);
        }
    }

    public registerTagsCacheListener(handler: () => void): void {
        this.tagsCacheListeners.push(handler);
    }

    public unregisterTagsCacheListener(handler: () => void): void {
        const index = this.tagsCacheListeners.indexOf(handler);
        if (index >= 0) {
            this.tagsCacheListeners.slice(index, 1);
        }
    }

    public searchTags = (repositoryId: string, projectId: string, searchText: string, continuationToken?: string): PagedResults<GitRef> =>
        this.searchPagedRefs(repositoryId, projectId, searchText, RefArea.tags, continuationToken);

    public getCommitsById(repositoryId: string, projectId: string, id: string): PagedResults<GitCommitRef> {
        if (!/^[0-9a-fA-F]+$/.test(id)) {
            const commits = new ReadyableObservableArray<GitCommitRef>([]);
            commits.ready.value = true;
            return { continuationToken: null, items: commits };
        }
        const upperBound = id + "f".repeat(40 - id.length);
        const lowerBound = id + "0".repeat(40 - id.length);

        const items = new ReadyableObservableArray<GitCommitRef>();
        getGitClient(this.pageContext)
            .getCommitsBatch(
                {
                    $top: 100,
                    fromCommitId: lowerBound,
                    toCommitId: upperBound
                } as GitQueryCommitsCriteria,
                repositoryId,
                projectId
            )
            .then(values => {
                items.value = values;
                items.ready.value = true;
            });

        return { continuationToken: null, items: items };
    }

    private getPagedRefs(repositoryId: string, projectId: string, refArea: RefArea): PagedResults<GitRef> {
        let refs = this.getCachedRefs(refArea, repositoryId);
        if (!refs) {
            const items = new ReadyableObservableArray<GitRef>();
            refs = { items };
            const client = getGitClient(this.pageContext);
            client
                .getRefs(repositoryId, projectId, refArea, undefined, undefined, undefined, undefined, undefined, undefined, pageSize)
                .then((pagedList: PagedList<GitRef>) => {
                    refs.continuationToken = pagedList.continuationToken;
                    items.value = pagedList;
                    items.ready.value = true;
                });
            this.cacheRefs(refArea, repositoryId, refs);
        }

        return refs;
    }

    private getMoreRefs(repositoryId: string, projectId: string, continuationToken: string, refArea: RefArea): PagedResults<GitRef> {
        const previousPagedResults = this.getCachedRefs(refArea, repositoryId);
        if (!previousPagedResults) {
            return this.getPagedRefs(repositoryId, projectId, refArea);
        }

        if (!previousPagedResults.continuationToken || previousPagedResults.continuationToken !== continuationToken) {
            return previousPagedResults;
        }

        const items = new ReadyableObservableArray<GitRef>();
        const results: PagedResults<GitRef> = { items: items };
        const client = getGitClient(this.pageContext);
        client
            .getRefs(
                repositoryId,
                projectId,
                refArea,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                pageSize,
                previousPagedResults.continuationToken
            )
            .then((pagedList: PagedList<GitRef>) => {
                results.continuationToken = pagedList.continuationToken;
                items.push(...pagedList);
                items.ready.value = true;
                this.cacheRefs(refArea, repositoryId, results);
            });

        return results;
    }

    private searchPagedRefs(
        repositoryId: string,
        projectId: string,
        searchText: string,
        refArea: RefArea,
        continuationToken?: string
    ): PagedResults<GitRef> {
        const refs = this.getCachedRefs(refArea, repositoryId);
        if (refs) {
            const pagedList = refs;
            if (!pagedList.continuationToken) {
                const filteredRefs = pagedList.items.value.filter(ref => ref.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
                const items = new ReadyableObservableArray<GitRef>(filteredRefs);
                items.ready.value = true;
                return { items };
            }
        }
        const items = new ReadyableObservableArray<GitRef>();
        const results: PagedResults<GitRef> = { items };

        getGitClient(this.pageContext)
            .getRefs(repositoryId, projectId, refArea, undefined, undefined, undefined, undefined, undefined, searchText, pageSize, continuationToken)
            .then((pagedList: PagedList<GitRef>) => {
                results.continuationToken = pagedList.continuationToken;
                items.value = pagedList;
                items.ready.value = true;
            });

        return results;
    }

    private getCachedRefs(refArea: RefArea, repositoryId: string): PagedResults<GitRef> {
        return this.refsByArea[refArea][repositoryId];
    }

    private cacheRefs(refArea: RefArea, repositoryId: string, refs: PagedResults<GitRef>): void {
        if (!this.refsByArea[refArea][repositoryId]) {
            this.refsByArea[refArea][repositoryId] = refs;
        } else {
            const results = this.refsByArea[refArea][repositoryId];
            const newItems = new ReadyableObservableArray<GitRef>([...results.items.value, ...refs.items.value]);
            newItems.ready.value = true;
            this.refsByArea[refArea][repositoryId] = { items: newItems, continuationToken: refs.continuationToken };
        }
        this.refsByArea[refArea][repositoryId] = refs;
    }
}

const serviceName = "IGitRefService";
export function getGitRefService(pageContext: IVssPageContext) {
    return pageContext.getService<IGitRefService>(serviceName);
}

Services.add(serviceName, { serviceFactory: GitRefService });
